import { Button, Flex, Loader, Text, TextInput, Title } from '@mantine/core';
import { AnimatePresence, motion, PanInfo } from 'motion/react';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import styles from './styles.module.css';

export const ManualCodeEntry = ({
	value,
	setValue,
	open,
	onClose,
	title,
	loading,
	instructions,
	shouldValidate,
	actions,
	error,
}: {
	value: string;
	setValue: React.Dispatch<React.SetStateAction<string>>;
	open: boolean;
	onClose: () => void;
	title?: string;
	instructions?: ReactNode | string;
	loading?: boolean;
	shouldValidate?: boolean;
	error?: string;
	actions?: (args: { isValid: boolean; onClose: () => void }) => ReactNode;
}) => {
	const [dragY, setDragY] = useState(0);

	// Reset drag state when drawer is closed
	useEffect(() => {
		if (!open) {
			setDragY(0);
		}
	}, [open]);

	const handleDragEnd = (_: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
		const threshold = 100; // Distance in pixels to trigger close
		if (info.offset.y > threshold) {
			onClose();
		} else {
			setDragY(0); // Reset position if not dragged far enough
		}
	};

	// TODO: Add the validation back in once we know what the possible codes are.
	const isValidInput = useMemo(() => {
		if (!shouldValidate) return true;
		return true;
	}, [shouldValidate]);

	return (
		<AnimatePresence>
			{open && (
				<motion.div
					key="manual-code-entry"
					className={styles.drawer}
					initial={{ y: '100%' }}
					animate={{
						y: dragY,
						transition: {
							type: 'spring',
							damping: 25,
							stiffness: 320,
							mass: 0.9,
							delay: 0.08,
						},
					}}
					exit={{
						y: '100%',
						transition: {
							type: 'spring',
							damping: 35,
							stiffness: 450,
							mass: 0.7,
							duration: 0.3,
						},
					}}
					drag="y"
					dragConstraints={{ top: 0, bottom: 300 }}
					dragElastic={0.2}
					onDrag={(_, info) => setDragY(info.offset.y)}
					onDragEnd={handleDragEnd}
				>
					{/* Drag Handle */}
					<div className={styles.dragHandle} />

					<Flex direction="column" gap="lg" pt="xl" px="lg">
						<Flex direction="column" gap="sm">
							<Title order={2}>{title || 'Manually Enter Code'}</Title>
							{instructions && (
								<>
									{typeof instructions === 'string' ? (
										<Text size="sm">{instructions}</Text>
									) : (
										instructions
									)}
								</>
							)}
						</Flex>
						<TextInput
							value={value}
							rightSection={loading && <Loader color="gray" size="sm" />}
							size="md"
							error={
								error && (
									<motion.p
										style={{ margin: 0, fontSize: '14px' }}
										initial={{ opacity: 0 }}
										animate={{ opacity: 1 }}
										exit={{ opacity: 0 }}
									>
										{error}
									</motion.p>
								)
							}
							onChange={(e) => setValue(e.target.value)}
							placeholder="Enter Code"
						/>
						{actions ? (
							actions({ isValid: isValidInput, onClose })
						) : (
							<Button bg="grey" variant="filled" size="md" c="white" onClick={onClose}>
								Close
							</Button>
						)}
					</Flex>
				</motion.div>
			)}
		</AnimatePresence>
	);
};
